import { useState } from "react";
import { UserCheck, CalendarDays, CalendarRange, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import { PageHeader, Kpi, Panel, Loading, Empty, Plate, Chip, ExportBtn, useFetch } from "@/components/common";
import { ComFilters } from "@/pages/commerciaux/ComDashboard";
import { useAuth } from "@/context/AuthContext";
import { fNum, fDate, fKm } from "@/lib/api";
import { HistBadge, PersoHistoryAdmin } from "@/components/PersoHistory";
import { UsageChip, PersoTypesSub } from "@/components/Conges";

const FLAG = {
  ok: null,
  eleve: ["Élevé", "bg-rose-50 text-rose-700 border-rose-200"],
  incoherent: ["Incohérent", "bg-rose-100 text-rose-800 border-rose-300"],
  incomplet: ["Incomplet", "bg-amber-50 text-amber-700 border-amber-200"],
  conges: ["Inclus dans les congés", "bg-sky-50 text-sky-700 border-sky-200"],
  en_cours: ["Congés en cours", "bg-sky-50 text-sky-700 border-sky-200"],
};

export default function KmPerso() {
  const { user } = useAuth();
  const admin = user.role === "admin";
  const [f, setF] = useState({});
  const [k, setK] = useState(0);
  const { data, loading } = useFetch("/commerciaux/weekends", { ...f, k });
  return (
    <div data-testid="km-perso-page">
      <PageHeader overline="Véhicules commerciaux" title="Kilométrage personnel">
        {admin && <button onClick={() => document.getElementById("perso-history-admin")?.scrollIntoView({ behavior: "smooth" })} className="h-10 px-4 rounded-md bg-[#0E2D43] hover:bg-[#163f5e] text-white text-sm font-semibold transition-colors" data-testid="goto-perso-history">Historique de KM personnels</button>}
        {admin && <ExportBtn path="/export/weekends" params={f} filename="kilometrage-personnel.xlsx" testid="export-weekends" />}
      </PageHeader>
      {admin && <ComFilters f={f} setF={setF} full={false} testid="km-perso-filters" />}
      {loading || !data ? <Loading /> : <Body d={data} admin={admin} />}
      {admin && <PersoHistoryAdmin onChanged={() => setK((x) => x + 1)} />}
    </div>
  );
}

function Body({ d, admin }) {
  const s = d.summary;
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <Kpi testid="kpi-perso-total" label="Total période" value={fKm(s.total)} sub={`${s.weeks} week-ends complets`} icon={UserCheck} tone="navy" />
        <Kpi testid="kpi-perso-month" label="Total mois en cours" value={fKm(s.month_total)} sub={PersoTypesSub({ t: s.month_by_type })} icon={CalendarDays} delay={1} />
        <Kpi testid="kpi-perso-year" label="Total année en cours" value={fKm(s.year_total)} sub={PersoTypesSub({ t: s.year_by_type })} icon={CalendarRange} delay={2} />
        <Kpi testid="kpi-perso-avg" label="Moyenne hebdomadaire" value={fKm(s.avg_week)} sub={`Seuil d'alerte : ${s.threshold} km`} icon={TrendingUp} delay={3} />
      </div>
      <Panel title="Évolution des km personnels par week-end" className="mb-6" testid="chart-perso-weekly">
        {!d.weekly_series.length ? <Empty /> : (
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={d.weekly_series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="week" tickFormatter={fDate} tick={{ fontSize: 10 }} minTickGap={20} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip labelFormatter={(l) => `Week-end du ${fDate(l)}`} formatter={(v) => `${fNum(v)} km`} />
                {!admin || d.rows.every((r) => r.user_id === d.rows[0]?.user_id) ? <ReferenceLine y={s.threshold} stroke="#e11d48" strokeDasharray="4 4" /> : null}
                <Bar dataKey="perso_km" name="Km perso" fill="#F49619" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Panel>
      <div className="grid xl:grid-cols-3 gap-6">
        <Panel title="Rapprochement vendredi / lundi" className="xl:col-span-2" flush testid="weekends-panel">
          {!d.rows.length ? <Empty /> : (
            <div className="overflow-x-auto max-h-[560px]">
              <table className="tbl w-full" data-testid="weekends-table">
                <thead className="sticky top-0"><tr><th>Période</th><th>Type</th>{admin && <th>Commercial</th>}<th>Véhicule</th><th>Début</th><th>Fin</th><th className="text-right">Km perso</th><th></th></tr></thead>
                <tbody>
                  {d.rows.map((r) => (
                    <tr key={`${r.user_id}-${r.key_date}-${r.conges_id || r.hist_id || "w"}`} data-testid={`weekend-row-${r.key_date}`}>
                      <td className="font-mono text-xs">{r.week_label}</td>
                      <td><UsageChip w={r} /></td>
                      {admin && <td className="font-semibold">{r.user_name}</td>}
                      <td><Plate>{r.plate}</Plate></td>
                      <td>{r.source === "historique" ? <span className="text-xs text-slate-500" title={r.comment || ""}>Source : {r.origin}</span> : r.friday_date ? <>{fDate(r.friday_date)} <span className="text-slate-400 tabular-nums">· {fNum(r.friday_km)}</span></> : <span className="text-amber-600">manquant</span>}</td>
                      <td>{r.source === "historique" ? <HistBadge w={r} /> : r.monday_date ? <>{fDate(r.monday_date)} <span className="text-slate-400 tabular-nums">· {fNum(r.monday_km)}</span></> : <span className="text-amber-600">{r.usage_type === "conges" ? "en cours" : "manquant"}</span>}</td>
                      <td className="text-right font-bold tabular-nums text-[#0E2D43]" title={r.excluded_reason || ""}>{r.perso_km === null ? "—" : fNum(r.perso_km)}</td>
                      <td>{FLAG[r.flag] && <Chip cls={FLAG[r.flag][1]}>{FLAG[r.flag][0]}</Chip>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
        <Panel title="Alertes" flush testid="perso-alerts">
          {!d.alerts.length ? <Empty text="Aucune alerte" /> : (
            <ul className="divide-y divide-slate-100 max-h-[560px] overflow-y-auto">
              {d.alerts.map((a) => (
                <li key={a.id} className="px-4 py-2.5 text-sm" data-testid={`perso-alert-${a.type}`}>
                  <div className="flex justify-between gap-2"><span className="font-semibold text-[#0E2D43]">{a.label}</span><span className="text-xs text-slate-400 shrink-0">{fDate(a.date)}</span></div>
                  <div className="text-xs text-slate-500 mt-0.5">{admin ? `${a.user_name} · ` : ""}{a.detail}</div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </>
  );
}
